import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { IUseCase } from '@core/base/use-case.interface';
import { AuthStorageService } from '@core/services/auth-storage.service';
import { LanguageStorageService } from '@core/services/language-storage.service';
import { UserSessionStorageService } from '@core/services/user-session-storage.service';
import { FtsAuthApiService } from '@core/services/api/fts-auth.api.service';
import { FtsUsersApiService } from '@core/services/api/fts-users.api.service';
import { IUserLogoutResponseDto } from '@core/interfaces/api/users/user-logout-response-dto.interface';

@Injectable({
  providedIn: 'root',
})
export class LogoutUseCaseService implements IUseCase<string, IUserLogoutResponseDto> {
  private readonly authStorageService = inject(AuthStorageService);
  private readonly languageStorageService = inject(LanguageStorageService);
  private readonly userSessionStorage = inject(UserSessionStorageService);
  private readonly userService = inject(FtsUsersApiService);
  private readonly authService = inject(FtsAuthApiService);

  execute(): Observable<IUserLogoutResponseDto> {
    return this.authService.logout().pipe(
      // Revoking the refresh token on the server is best effort; the local session ends either way.
      catchError(() => of(undefined)),
      switchMap(() => this.userService.logout()),
      tap({ next: () => this.clearLocalSession(), error: () => this.clearLocalSession() })
    );
  }

  private clearLocalSession(): void {
    this.authStorageService.clearAuthData();
    this.languageStorageService.clear();
    this.userSessionStorage.clear();
  }
}
